import { isUserRejection } from './signing-errors'
import {
	WALLET_TELEMETRY_SCHEMA_VERSION,
	type WalletTelemetryErrorCategory,
	type WalletTelemetryEvent,
	type WalletTelemetryOutcome,
	type WalletTelemetryPayload,
	type WalletTelemetryProvider,
	type WalletTelemetryStage,
	type WalletTelemetrySurface
} from './telemetry-schema'

const TELEMETRY_ENDPOINT = '/api/telemetry/wallet'
const MAX_EVENTS_PER_MINUTE = 30
const DUPLICATE_WINDOW_MS = 10_000
const DEFAULT_STALL_MS = 10_000

const eventTimes: number[] = []
const recentEvents = new Map<string, number>()

export interface WalletTelemetryError extends Error {
	code?: string | number
	telemetryCategory?: WalletTelemetryErrorCategory
	telemetryStage?: WalletTelemetryStage
	userMessage?: string
}

export interface WalletFlow {
	flowId: string
	event: WalletTelemetryEvent
	provider: WalletTelemetryProvider
	surface: WalletTelemetrySurface
	startedAt: number
}

type WalletTelemetryDetails = Partial<
	Pick<
		WalletTelemetryPayload,
		| 'duration_bucket'
		| 'provider_detected'
		| 'api_capabilities'
		| 'provider_version'
		| 'error_category'
		| 'provider_error_code'
		| 'collection_slug'
		| 'asset_protocol'
		| 'offer_scope'
		| 'offer_api'
		| 'offer_ref'
		| 'item_count_bucket'
		| 'protected'
		| 'attempt_id'
		| 'crash_message'
		| 'crash_path'
		| 'crash_frame'
		| 'crash_components'
		| 'crash_action'
		| 'crash_dom'
		| 'trail'
		| 'buyer_address'
		| 'page_translated'
		| 'seller_address'
		| 'error_detail'
		| 'balance_sats'
		| 'price_sats'
		| 'fee_rate'
		| 'path'
	>
>

export const sanitizeCollectionSlug = (value: unknown): string | undefined => {
	try {
		if (typeof value !== 'string') return undefined

		const sanitized = decodeURIComponent(value.trim())
			.normalize('NFKC')
			.toLowerCase()
			.replace(/[^a-z0-9._$-]+/g, '-')
			.replace(/-+/g, '-')
			.replace(/^[^a-z0-9]+|[^a-z0-9]+$/g, '')
			.slice(0, 96)

		return sanitized || undefined
	} catch {
		return undefined
	}
}

export const itemCountBucket = (itemCount: number): WalletTelemetryPayload['item_count_bucket'] => {
	try {
		if (itemCount > 10) return '11+'
		if (itemCount > 5) return '6-10'
		if (itemCount > 1) return '2-5'
		return '1'
	} catch {
		return '1'
	}
}

const now = (): number => {
	try {
		return typeof performance !== 'undefined' ? performance.now() : Date.now()
	} catch {
		return 0
	}
}

const createFlowId = (): string => {
	try {
		if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
			return crypto.randomUUID()
		}

		if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
			const bytes = crypto.getRandomValues(new Uint8Array(16))
			return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')
		}
	} catch {}

	try {
		return `wallet-${Date.now().toString(36)}`
	} catch {
		return 'wallet-fallback'
	}
}

/** Coarse OS family plus whether we are inside a wallet app's webview. */
const platformDetails = (): Pick<WalletTelemetryPayload, 'os_family' | 'in_app_browser'> => {
	try {
		if (typeof navigator === 'undefined') return {}
		const ua = navigator.userAgent || ''
		const os: WalletTelemetryPayload['os_family'] =
			/iPhone|iPad|iPod/i.test(ua) ||
			(/Macintosh/i.test(ua) && (navigator as any).maxTouchPoints > 1)
				? 'ios'
				: /Android/i.test(ua)
				? 'android'
				: /Macintosh|Mac OS X/i.test(ua)
				? 'macos'
				: /Windows/i.test(ua)
				? 'windows'
				: /Linux|CrOS/i.test(ua)
				? 'linux'
				: 'other'
		const inApp =
			/\bwv\b|XverseApp|UniSat|OKApp|okx|Phantom|MetaMaskMobile|Leather|Magic\s?Eden/i.test(ua)
		return { os_family: os, ...(inApp ? { in_app_browser: true } : {}) }
	} catch {
		return {}
	}
}

const browserDetails = (): Pick<WalletTelemetryPayload, 'browser_family' | 'browser_major'> => {
	try {
		if (typeof navigator === 'undefined') {
			return { browser_family: 'other' }
		}

		const userAgent = navigator.userAgent
		let browser_family: WalletTelemetryPayload['browser_family'] = 'other'
		let versionMatch: RegExpMatchArray | null = null

		// A wallet app's embedded browser: iOS WebKit with no Safari token, or
		// Android's "; wv)" marker. Named, because purchases from inside Xverse
		// or UniSat behave differently from a real browser.
		const iosWebView =
			/iPhone|iPad|iPod/i.test(userAgent) &&
			/AppleWebKit/i.test(userAgent) &&
			!/Safari\//i.test(userAgent)
		const androidWebView = /Android/i.test(userAgent) && /\bwv\b/.test(userAgent)
		if (iosWebView || androidWebView) {
			browser_family = 'webview'
			versionMatch = userAgent.match(/(?:Chrome|Version)\/(\d+)/i)
		} else if (/Brave/i.test(userAgent) || 'brave' in navigator) {
			browser_family = 'brave'
			versionMatch = userAgent.match(/Chrome\/(\d+)/i)
		} else if (/Edg\//i.test(userAgent)) {
			browser_family = 'edge'
			versionMatch = userAgent.match(/Edg\/(\d+)/i)
		} else if (/Chrome\//i.test(userAgent)) {
			browser_family = 'chrome'
			versionMatch = userAgent.match(/Chrome\/(\d+)/i)
		} else if (/Firefox\//i.test(userAgent)) {
			browser_family = 'firefox'
			versionMatch = userAgent.match(/Firefox\/(\d+)/i)
		} else if (/Safari\//i.test(userAgent)) {
			browser_family = 'safari'
			versionMatch = userAgent.match(/Version\/(\d+)/i)
		}

		const browser_major = versionMatch ? Number(versionMatch[1]) : undefined
		return Number.isInteger(browser_major) ? { browser_family, browser_major } : { browser_family }
	} catch {
		return { browser_family: 'other' }
	}
}

const shouldSend = (payload: WalletTelemetryPayload): boolean => {
	try {
		const timestamp = Date.now()
		while (eventTimes.length > 0 && eventTimes[0] < timestamp - 60_000) {
			eventTimes.shift()
		}

		// Never discard the terminal revenue event. The client budget is only a
		// noise guard; real abuse protection belongs at the route/WAF layer.
		const isTerminalMarketEvent =
			(payload.event === 'purchase_submit' ||
				payload.event.startsWith('offer_') ||
				(!!payload.attempt_id &&
					['listing_build', 'listing_sign', 'listing_authorize'].includes(payload.event))) &&
			payload.outcome !== 'started'

		if (!isTerminalMarketEvent && eventTimes.length >= MAX_EVENTS_PER_MINUTE) {
			return false
		}

		const duplicateKey = [
			payload.flow_id,
			payload.event,
			payload.stage,
			payload.outcome,
			payload.error_category ?? '',
			payload.provider_error_code ?? ''
		].join(':')
		const lastSentAt = recentEvents.get(duplicateKey)

		if (lastSentAt && lastSentAt > timestamp - DUPLICATE_WINDOW_MS) {
			return false
		}

		if (!isTerminalMarketEvent) eventTimes.push(timestamp)
		recentEvents.set(duplicateKey, timestamp)
		for (const [key, sentAt] of recentEvents) {
			if (sentAt < timestamp - DUPLICATE_WINDOW_MS) {
				recentEvents.delete(key)
			}
		}

		return true
	} catch {
		return false
	}
}

const emitWalletTelemetry = (
	flow: WalletFlow,
	stage: WalletTelemetryStage,
	outcome: WalletTelemetryOutcome,
	details: WalletTelemetryDetails = {}
): void => {
	try {
		if (typeof window === 'undefined' || typeof fetch !== 'function') {
			return
		}
		// A crash has no "started" phase; the route rejects one, so never send it.
		if (flow.event === 'client_crash' && outcome === 'started') {
			return
		}

		const payload: WalletTelemetryPayload = {
			...(BUILD_SHA ? { app_build: BUILD_SHA } : {}),
			schema_version: WALLET_TELEMETRY_SCHEMA_VERSION,
			telemetry: 'wallet_flow',
			flow_id: flow.flowId,
			event: flow.event,
			stage,
			outcome,
			provider: flow.provider,
			surface: flow.surface,
			network: 'BTC',
			...browserDetails(),
			...platformDetails(),
			device_family:
				typeof navigator !== 'undefined' && /Android|iPhone|iPad|Mobile/i.test(navigator.userAgent)
					? 'mobile'
					: 'desktop',
			...details
		}

		if (!shouldSend(payload)) {
			return
		}

		const delivery = fetch(TELEMETRY_ENDPOINT, {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify(payload),
			cache: 'no-store',
			credentials: 'omit',
			keepalive: true
		})
		void Promise.resolve(delivery).catch(() => undefined)
	} catch {}
}

export const beginWalletFlow = (
	event: WalletTelemetryEvent,
	provider: WalletTelemetryProvider,
	surface: WalletTelemetrySurface,
	stage: WalletTelemetryStage,
	details: WalletTelemetryDetails = {}
): WalletFlow => {
	try {
		const flow: WalletFlow = {
			flowId: createFlowId(),
			event,
			provider,
			surface,
			startedAt: now()
		}
		emitWalletTelemetry(flow, stage, 'started', details)
		return flow
	} catch {
		return {
			flowId: 'wallet-fallback',
			event,
			provider,
			surface,
			startedAt: 0
		}
	}
}

const durationBucket = (startedAt: number): WalletTelemetryPayload['duration_bucket'] => {
	try {
		const duration = Math.max(0, now() - startedAt)
		if (duration < 250) return '<250ms'
		if (duration < 1000) return '250ms-1s'
		if (duration < 5000) return '1-5s'
		if (duration < 15_000) return '5-15s'
		return '15s+'
	} catch {
		return '15s+'
	}
}

/**
 * The phases one attempt has been through, oldest first. Kept in memory for
 * the tab's lifetime (an attempt is a few seconds; the map is capped), and
 * attached as `trail` to the failing phase so the #errors line reads as the
 * whole story: "build+ > sign-(wallet_account_mismatch)".
 */
const attemptTrails = new Map<string, string[]>()
const TRAIL_STAGE_NAMES: Record<string, string> = {
	build_psbt: 'build',
	sign_psbts: 'sign',
	authorize_listing: 'authorize',
	submit_purchase: 'submit',
	sign_setup: 'sign_setup',
	sign_purchase: 'sign_purchase',
	build_offer: 'build',
	sign_funding: 'sign_funding',
	prepare_offer: 'prepare',
	sign_offer: 'sign_offer',
	activate_offer: 'activate',
	check_offer: 'check',
	sign_accept: 'sign',
	submit_accept: 'submit',
	sign_refund: 'sign',
	submit_refund: 'submit',
	submit_decline: 'decline'
}
const noteTrailStep = (
	stage: WalletTelemetryStage,
	outcome: WalletTelemetryOutcome,
	details: WalletTelemetryDetails
): string | undefined => {
	const attempt = details.attempt_id
	if (!attempt) return undefined
	if (attemptTrails.size > 200) attemptTrails.clear()
	const steps = attemptTrails.get(attempt) ?? []
	const mark =
		outcome === 'success' ? '+' : outcome === 'cancelled' ? '~' : outcome === 'failure' ? '-' : '?'
	const code =
		outcome === 'failure' && details.provider_error_code ? `(${details.provider_error_code})` : ''
	const name = `${details.protected === false ? 'std:' : ''}${TRAIL_STAGE_NAMES[stage] ?? stage}`
	steps.push(`${name}${mark}${code}`)
	if (steps.length > 12) steps.shift()
	attemptTrails.set(attempt, steps)
	// Only a failure carries the trail (the route accepts it on failures only).
	return outcome === 'failure' ? steps.join(' > ') : undefined
}

export const finishWalletFlow = (
	flow: WalletFlow,
	stage: WalletTelemetryStage,
	outcome: Exclude<WalletTelemetryOutcome, 'started' | 'stalled'>,
	details: WalletTelemetryDetails = {}
): void => {
	try {
		const trail = noteTrailStep(stage, outcome, details)
		emitWalletTelemetry(flow, stage, outcome, {
			duration_bucket: durationBucket(flow.startedAt),
			...details,
			...(trail ? { trail } : {})
		})
	} catch {}
}

export const createWalletStallWatchdog = (
	flow: WalletFlow,
	stage: WalletTelemetryStage,
	onStalled?: () => void,
	timeoutMs: number = DEFAULT_STALL_MS
): (() => void) => {
	try {
		const timer = globalThis.setTimeout(() => {
			try {
				emitWalletTelemetry(flow, stage, 'stalled', {
					duration_bucket: durationBucket(flow.startedAt)
				})
				const result = onStalled?.()
				void Promise.resolve(result).catch(() => undefined)
			} catch {}
		}, timeoutMs)

		return () => {
			try {
				globalThis.clearTimeout(timer)
			} catch {}
		}
	} catch {
		return () => undefined
	}
}

export const providerSlug = (providerName: string): WalletTelemetryProvider => {
	try {
		const normalized = providerName.toLowerCase()
		if (normalized === 'tap' || normalized.includes('tap wallet')) return 'tap'
		if (normalized.includes('ordinals wallet')) return 'ordinalswallet'
		if (normalized.includes('unisat')) return 'unisat'
		if (normalized.includes('okx')) return 'okx'
		if (normalized.includes('oyl')) return 'oyl'
		if (normalized.includes('xverse')) return 'xverse'
		if (normalized.includes('phantom')) return 'phantom'
		if (normalized === 'hiro' || normalized.includes('leather')) return 'leather'
		if (normalized.includes('doge')) return 'dogelabs'
		if (normalized.includes('nintondo')) return 'nintondo'
		return 'unknown'
	} catch {
		return 'unknown'
	}
}

export const classifyWalletError = (error: any): WalletTelemetryErrorCategory => {
	try {
		if (error?.telemetryCategory) {
			return error.telemetryCategory
		}

		if (isUserRejection(error)) {
			return 'user_rejected'
		}

		const code = String(error?.code ?? error?.error?.code ?? '').toLowerCase()
		// Our own sign/connect codes are exact; do not sniff their text.
		switch (code) {
			case 'wallet_probe_timeout':
				return 'timeout'
			case 'wallet_extension_conflict':
				return 'provider_missing'
			case 'wallet_connect_rejected':
			case 'wallet_not_connected':
				return 'wallet_locked'
			case 'provider_missing':
				return 'provider_missing'
			case 'wallet_account_mismatch':
				return 'invalid_response'
		}
		const message = [
			error?.message,
			error?.error?.message,
			error?.reason,
			error?.response?.data?.message,
			typeof error?.response?.data === 'string' ? error.response.data : undefined
		]
			.filter((value) => typeof value === 'string')
			.join(' ')
			.toLowerCase()

		if (
			message.includes('insufficient funds') ||
			message.includes('insufficient balance') ||
			message.includes('not enough funds') ||
			message.includes('not enough bitcoin')
		) {
			return 'insufficient_funds'
		}

		if (code === '4100' || message.includes('unauthor') || message.includes('permission')) {
			return 'permission_denied'
		}
		if (message.includes('not installed') || message.includes('not detected')) {
			return 'provider_missing'
		}
		if (message.includes('not a function') || message.includes('unsupported')) {
			return 'method_missing'
		}
		// Word match: 'block', 'locktime' and 'clock' are not a locked wallet.
		if (/\b(locked|unlock|unlocked)\b/.test(message) || message.includes('password')) {
			return 'wallet_locked'
		}
		if (message.includes('timeout') || message.includes('timed out')) {
			return 'timeout'
		}
		if (message.includes('network') || message.includes('fetch')) {
			return 'network'
		}
		if (
			message.includes('account') ||
			message.includes('address') ||
			message.includes('public key') ||
			message.includes('response')
		) {
			return 'invalid_response'
		}

		return 'unknown'
	} catch {
		return 'unknown'
	}
}

export const walletErrorDetails = (error: any): WalletTelemetryDetails => {
	try {
		const rawCode = error?.code ?? error?.error?.code
		const provider_error_code =
			rawCode === undefined
				? undefined
				: String(rawCode)
						.replace(/[^A-Za-z0-9_.-]/g, '')
						.slice(0, 32) || undefined

		return {
			error_category: classifyWalletError(error),
			provider_error_code
		}
	} catch {
		return { error_category: 'unknown' }
	}
}

export const walletErrorStage = (error: any): WalletTelemetryStage => {
	try {
		return error?.telemetryStage ?? 'unknown'
	} catch {
		return 'unknown'
	}
}

const ERROR_DETAIL_PATTERN = /^[A-Za-z0-9_.,:;=()\[\]/ '"·—-]{1,200}$/

/**
 * Bounded text of what actually went wrong: the API's message, detail and
 * ref, or the thrown message. Long hex ids and addresses collapse to `:id`
 * so the line stays about the failure, not the user.
 */
export const errorDetail = (error: any): string | undefined => {
	try {
		const data = error?.response?.data
		const parts = [
			typeof data?.message === 'string' ? data.message : undefined,
			typeof data?.detail === 'string' ? data.detail : undefined,
			typeof data?.ref === 'string' ? `ref ${data.ref.slice(0, 8)}` : undefined,
			!data && typeof error?.message === 'string' ? error.message : undefined,
			error?.response?.status ? `http ${error.response.status}` : undefined
		].filter(Boolean)
		if (parts.length === 0) return undefined
		const text = parts
			.join(' — ')
			.replace(/\s+/g, ' ')
			.replace(/\b(bc1|[13])[a-z0-9]{20,}\b/gi, ':id')
			.replace(/\b[0-9a-f]{16,}(i\d+|:\d+)?\b/gi, ':id')
			.replace(/[^A-Za-z0-9_.,:;=()\[\]/ '"·—-]/g, '')
			.trim()
			.slice(0, 200)
		return ERROR_DETAIL_PATTERN.test(text) ? text : undefined
	} catch {
		return undefined
	}
}

/** Current route with ids collapsed, for failure telemetry. */
export const telemetryPath = (): string | undefined => {
	try {
		if (typeof window === 'undefined') return undefined
		const pathname = window.location?.pathname
		if (typeof pathname !== 'string' || !pathname.startsWith('/')) return undefined
		const collapsed = pathname
			.split('/')
			.map((segment) =>
				segment.length > 24 ||
				/^[0-9a-f]{16,}/i.test(segment) ||
				/^(bc1|[13])[a-z0-9]{20,}$/i.test(segment)
					? ':id'
					: segment
			)
			.join('/')
			.slice(0, 120)
		return /^[A-Za-z0-9_./:-]{1,120}$/.test(collapsed) ? collapsed : undefined
	} catch {
		return undefined
	}
}

const BUILD_SHA = /^[0-9a-f]{7,40}$/.test(String(process.env.NEXT_PUBLIC_BUILD_SHA || ''))
	? String(process.env.NEXT_PUBLIC_BUILD_SHA).slice(0, 12)
	: ''

const ADDRESS_PATTERN = /^(bc1[a-z0-9]{25,90}|[13][a-km-zA-HJ-NP-Z1-9]{25,40})$/

/** A bitcoin address as-is, or nothing: never a partial or garbage value. */
export const telemetryAddress = (value: unknown): string | undefined =>
	typeof value === 'string' && ADDRESS_PATTERN.test(value) ? value : undefined

export const walletErrorMessage = (error: any, providerName: string): string => {
	try {
		if (error?.userMessage) {
			return error.userMessage
		}

		switch (classifyWalletError(error)) {
			case 'user_rejected':
				return `${providerName} connection was cancelled.`
			case 'provider_missing':
				return `${providerName} was not detected. Enable it for this site, refresh, and try again.`
			case 'method_missing':
				return `${providerName} is out of date. Update the extension, refresh, and try again.`
			case 'wallet_locked':
				return `Unlock ${providerName}, then try again.`
			case 'permission_denied':
				return `${providerName} did not grant access to Ordinals Wallet. Try again and approve the connection.`
			case 'timeout':
				return `${providerName} did not respond. Open the extension, unlock it, and try again.`
			case 'invalid_response':
				return `${providerName} returned an invalid account. Confirm it has a Bitcoin Taproot account, then try again.`
			default:
				return `${providerName} could not connect. Open the extension, unlock it, and try again.`
		}
	} catch {
		return 'Wallet could not connect. Open the extension, unlock it, and try again.'
	}
}

export const tapProviderDetails = (): WalletTelemetryDetails => {
	try {
		if (typeof window === 'undefined') return {}

		const provider = (window as any).tapprotocol
		if (!provider) {
			return { provider_detected: false, api_capabilities: 0 }
		}

		let api_capabilities = 0
		if (typeof provider.requestAccounts === 'function') api_capabilities |= 1
		if (typeof provider.getAccounts === 'function') api_capabilities |= 2
		if (typeof provider.getPublicKey === 'function') api_capabilities |= 4
		if (typeof provider.getNetwork === 'function') api_capabilities |= 8
		if (typeof provider.signPsbt === 'function') api_capabilities |= 16
		if (typeof provider.signPsbts === 'function') api_capabilities |= 32
		if (typeof provider.on === 'function') api_capabilities |= 64

		const rawVersion = typeof provider.version === 'string' ? provider.version : undefined
		const provider_version = rawVersion?.replace(/[^A-Za-z0-9._-]/g, '').slice(0, 24)

		return {
			provider_detected: true,
			api_capabilities,
			provider_version: provider_version || undefined
		}
	} catch {
		return {}
	}
}

export const connectWalletWithTelemetry = async (
	provider: { name: string; connect: () => Promise<boolean> },
	surface: Extract<WalletTelemetrySurface, 'command_palette' | 'connect_modal'>,
	onStalled?: () => void
): Promise<boolean> => {
	let providerName = 'Unknown Wallet'
	try {
		providerName = provider.name
	} catch {}

	const slug = providerSlug(providerName)
	const providerDetails = slug === 'tap' ? tapProviderDetails() : {}
	const flow = beginWalletFlow('connect', slug, surface, 'wallet_selected', providerDetails)
	const clearWatchdog = createWalletStallWatchdog(flow, 'request_accounts', onStalled)

	let connected: boolean
	try {
		connected = await provider.connect()
	} catch (error) {
		clearWatchdog()
		const category = classifyWalletError(error)
		finishWalletFlow(
			flow,
			walletErrorStage(error),
			category === 'user_rejected' ? 'cancelled' : 'failure',
			{
				...(slug === 'tap' ? tapProviderDetails() : {}),
				...walletErrorDetails(error)
			}
		)
		throw error
	}

	clearWatchdog()

	if (!connected) {
		const error = new Error(`${providerName} provider not detected`) as WalletTelemetryError
		error.telemetryCategory = 'provider_missing'
		error.telemetryStage = 'provider_probe'
		finishWalletFlow(flow, error.telemetryStage, 'failure', {
			...(slug === 'tap' ? tapProviderDetails() : {}),
			...walletErrorDetails(error)
		})
		throw error
	}

	finishWalletFlow(flow, 'connected', 'success', {
		...(slug === 'tap' ? tapProviderDetails() : {})
	})
	return connected
}
